<template>
  <div class="bot-test-snapshot">
    <!-- Loading state -->
    <div v-if="loading" class="flex items-center justify-center min-h-screen">
      <div class="text-center">
        <div class="flex justify-center mb-4">
          <ProgressSpinner
            style="width: 3rem; height: 3rem"
            stroke-width="3"
            :dt="SPINNER_DT"
            :aria-label="$t('common.loading')"
          />
        </div>
        <p class="text-gray-600">{{ $t('botEditor.testSnapshot.loadingWidget') }}</p>
      </div>
    </div>

    <!-- Error state -->
    <div v-else-if="error" class="flex items-center justify-center min-h-screen">
      <div class="text-center max-w-md mx-auto p-6">
        <div class="mb-4">
          <span class="material-icons text-red-500" style="font-size: 48px" aria-hidden="true">warning</span>
        </div>
        <h2 class="text-xl font-semibold text-gray-900 mb-2">{{ $t('common.error') }}</h2>
        <p class="text-gray-600">{{ error }}</p>
      </div>
    </div>

    <!-- Widget container -->
    <div v-else ref="widgetContainer" class="min-h-screen"></div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import api from '../../services/api';

const { t } = useI18n();
const route = useRoute();
const loading = ref(true);
const error = ref(null);
const widgetContainer = ref(null);
let scriptElement = null;

/**
 * Per-instance spinner tokens: this page is white, and the preset's dark-scheme
 * {primary.400} stops are ~1.8:1 on white. {primary.600} (cyan-600) is ~3.7:1.
 * Same key shape as the preset's `progressspinner` block. A setup-level constant keeps
 * the object identity stable, so PrimeVue's `dt` watcher injects the scoped
 * styles once.
 */
const SPINNER_DT = {
  colorScheme: {
    dark: {
      root: {
        colorOne: '{primary.600}',
        colorTwo: '{primary.600}',
        colorThree: '{primary.600}',
        colorFour: '{primary.600}'
      }
    }
  }
};

const loadWidgetScript = async () => {
  const botId = route.params.id;
  const workspaceId = route.params.workspaceId;

  if (!botId) {
    error.value = t('botEditor.testSnapshot.missingBotId');
    loading.value = false;
    return;
  }

  try {
    // Verifica prima che il bot esista e abbia uno snapshot pubblicato
    const response = await api.get(`/workspaces/${workspaceId}/bots/${botId}/production-status`);
    const status = response.data.data;

    if (!status || !status.currentDeployed) {
      error.value = t('botEditor.testSnapshot.noPublishedSnapshot');
      loading.value = false;
      return;
    }

    const scriptUrl = `${window.location.origin}/api/v1/bots/${botId}/widget.js?ts=${Date.now()}`;
    
    // Check if script already exists
    const existingScript = document.querySelector(`script[src*="/bots/${botId}/widget.js"]`);
    if (existingScript) {
      existingScript.remove();
    }

    // Create and load script
    scriptElement = document.createElement('script');
    scriptElement.src = scriptUrl;
    scriptElement.async = true;
    
    scriptElement.onload = () => {
      Console.log('[BotTestSnapshot] Widget script loaded successfully');
      loading.value = false;
    };
    
    scriptElement.onerror = () => {
      Console.error('[BotTestSnapshot] Failed to load widget script');
      error.value = t('botEditor.testSnapshot.botNotAvailable');
      loading.value = false;
    };

    document.head.appendChild(scriptElement);
  } catch (err) {
    Console.error('[BotTestSnapshot] Error loading widget:', err);
    error.value = t('botEditor.testSnapshot.widgetLoadError');
    loading.value = false;
  }
};

onMounted(() => {
  loadWidgetScript();
});

onUnmounted(() => {
  // Cleanup script element on unmount
  if (scriptElement && scriptElement.parentNode) {
    scriptElement.parentNode.removeChild(scriptElement);
  }
  
  // Remove the widget wrap that gets appended to body
  const widgetContainers = document.querySelectorAll('.newc-widget-wrap');
  widgetContainers.forEach(container => {
    container.remove();
  });
  
  // Remove any other widget elements that might be in the DOM
  const widgetButtons = document.querySelectorAll('.newc-widget-btn');
  widgetButtons.forEach(button => {
    button.remove();
  });
  
  const widgetFrames = document.querySelectorAll('.newc-widget-panel');
  widgetFrames.forEach(frame => {
    frame.remove();
  });
  
  Console.log('[BotTestSnapshot] Widget cleanup completed');
});
</script>

<style scoped>
.bot-test-snapshot {
  width: 100%;
  min-height: 100vh;
  background-color: #ffffff;
}
</style>
